import {
  defaultScenarioConfig,
  type AttackerDoctrine,
  type ScenarioConfig,
  type SimMetrics,
  type TargetDistribution,
} from "@air-defence-sim/sim-core";
import { rangeControl, renderHeading, selectControl } from "./controls.js";
import { PRESETS, type Preset } from "./presets.js";
import {
  drawFixedDefence,
  drawMobileDefence,
  drawSensor,
  drawStrikeWeapon,
  drawTarget,
} from "./sprites.js";
import { getPalette, type ThemeName } from "./theme.js";

export type LegendKind = "target" | "fixed" | "mobile" | "sensor" | "weapon";

export type UICallbacks = {
  onRun: () => void;
  onReplay: () => void;
  onHighlight: (kind: LegendKind | null) => void;
  onSpeedChange: (speed: number) => void;
  onTrailsChange: (on: boolean) => void;
  onThemeChange: (theme: ThemeName) => void;
  onCleanViewChange: (on: boolean) => void;
  onPauseToggle: () => void;
  onSeek: (t: number) => void;
  onExportPng: () => void;
  onJumpToContact: () => void;
  /** Build + copy a share URL for the current config; resolves to copy success. */
  onShareLink: () => Promise<boolean>;
};

const SPEED_OPTIONS = [0.5, 1, 2, 4, 8];
const DEFAULT_SPEED = 1;
const DEFAULT_THEME: ThemeName = "light";

export type UIControls = {
  getConfig: () => ScenarioConfig;
  getSpeed: () => number;
  getTrails: () => boolean;
  getTheme: () => ThemeName;
  setMetrics: (m: SimMetrics | null) => void;
  setStatus: (s: string) => void;
  setReplayEnabled: (enabled: boolean) => void;
  setPlaybackBounds: (end: number) => void;
  setPlaybackTime: (t: number) => void;
  setPlaying: (on: boolean) => void;
  setExportEnabled: (enabled: boolean) => void;
  setContactEnabled: (enabled: boolean) => void;
  /** Apply config values (e.g. from a share URL), updating every control. */
  applyConfig: (cfg: Partial<ScenarioConfig>) => void;
};

type Setter<T> = (v: T) => void;

export function mountUI(
  controlsEl: HTMLElement,
  scorecardEl: HTMLElement,
  overlayEl: HTMLElement,
  toolbarEl: HTMLElement,
  cb: UICallbacks,
): UIControls {
  const state: ScenarioConfig = defaultScenarioConfig({ seed: 1 });
  let currentSpeed = DEFAULT_SPEED;
  let trailsOn = false;
  let theme: ThemeName = DEFAULT_THEME;
  let cleanView = false;

  controlsEl.innerHTML = "";

  const presetSection = document.createElement("div");
  presetSection.className = "preset-section";
  presetSection.appendChild(renderHeading("Scenario preset"));
  const presetSelect = document.createElement("select");
  presetSelect.className = "preset-select";
  for (const p of PRESETS) {
    const opt = document.createElement("option");
    opt.value = p.id;
    opt.textContent = p.label;
    presetSelect.appendChild(opt);
  }
  const customOpt = document.createElement("option");
  customOpt.value = "__custom__";
  customOpt.textContent = "— Custom —";
  customOpt.disabled = true;
  presetSelect.appendChild(customOpt);
  presetSection.appendChild(presetSelect);
  const presetDesc = document.createElement("p");
  presetDesc.className = "preset-desc";
  presetSection.appendChild(presetDesc);
  controlsEl.appendChild(presetSection);

  controlsEl.appendChild(renderHeading("Scenario"));

  const setters: Array<Setter<unknown>> = [];
  const trackedRange = <K extends keyof ScenarioConfig>(
    label: string,
    key: K,
    min: number,
    max: number,
    step: number,
    format: (v: number) => string = (v) => `${v}`,
    cast: (v: number) => ScenarioConfig[K] = (v) => v as ScenarioConfig[K],
  ): void => {
    const initial = state[key] as number;
    const r = rangeControl(label, initial, min, max, step, (v) => {
      state[key] = cast(v);
      markCustom();
    }, format);
    setters.push(((v: number) => r.set(v)) as Setter<unknown>);
    setterByKey.set(key as string, ((v: number) => r.set(v)) as Setter<unknown>);
    controlsEl.appendChild(r.element);
  };
  const setterByKey = new Map<string, Setter<unknown>>();
  const trackedSelect = <T extends string, K extends keyof ScenarioConfig>(
    label: string,
    key: K,
    options: Array<[T, string]>,
  ): void => {
    const initial = state[key] as unknown as T;
    const s = selectControl<T>(label, initial, options, (v) => {
      state[key] = v as unknown as ScenarioConfig[K];
      markCustom();
    });
    setterByKey.set(key as string, ((v: T) => s.set(v)) as Setter<unknown>);
    controlsEl.appendChild(s.element);
  };

  trackedRange("Defence budget", "totalDefenceBudget", 0, 4000, 100);
  trackedRange("Mobile share", "mobileDefenceBudgetShare", 0, 1, 0.05, (v) => `${Math.round(v * 100)}%`);
  trackedSelect<TargetDistribution, "targetDistribution">(
    "Target distribution",
    "targetDistribution",
    [
      ["country", "Country (capital + towns)"],
      ["concentrated", "Concentrated (one zone)"],
      ["clustered", "Clustered (a few zones)"],
      ["distributed", "Distributed (across map)"],
    ],
  );
  trackedRange("Target value entropy", "targetValueEntropy", 0, 1, 0.05, (v) => v.toFixed(2));
  trackedRange("Attacker concentration", "attackerConcentration", 0, 1, 0.05, (v) => v.toFixed(2));
  trackedSelect<AttackerDoctrine, "attackerDoctrine">(
    "Attacker doctrine",
    "attackerDoctrine",
    [
      ["concentrate", "Concentrate"],
      ["disperse", "Disperse"],
      ["probe", "Probe"],
      ["mixed", "Mixed"],
    ],
  );
  trackedRange("Warning range", "warningRangeMultiplier", 0.25, 3, 0.25, (v) => `${v.toFixed(2)}×`);
  trackedRange("Weapons per wave", "attackerWeaponsPerWave", 2, 32, 1, (v) => `${Math.round(v)}`, (v) => Math.round(v));
  trackedRange("Wave count", "attackerWaveCount", 1, 8, 1, (v) => `${Math.round(v)}`, (v) => Math.round(v));
  trackedRange("Seed", "seed", 1, 100, 1, (v) => `${Math.round(v)}`, (v) => Math.round(v));

  let isApplyingPreset = false;
  function markCustom(): void {
    if (isApplyingPreset) return;
    presetSelect.value = "__custom__";
    presetDesc.textContent = "Custom configuration.";
  }
  function applyPreset(p: Preset): void {
    isApplyingPreset = true;
    for (const [k, v] of Object.entries(p.config)) {
      (state as unknown as Record<string, unknown>)[k] = v;
      const setter = setterByKey.get(k);
      if (setter) setter(v as never);
    }
    presetDesc.textContent = p.description;
    isApplyingPreset = false;
  }
  presetSelect.addEventListener("change", () => {
    const id = presetSelect.value;
    const preset = PRESETS.find((p) => p.id === id);
    if (preset) applyPreset(preset);
  });
  const initialPreset = PRESETS[0];
  if (initialPreset) {
    applyPreset(initialPreset);
    presetSelect.value = initialPreset.id;
  }

  const btnRow = document.createElement("div");
  btnRow.className = "btn-row";
  const runBtn = document.createElement("button");
  runBtn.textContent = "Run simulation";
  runBtn.addEventListener("click", () => cb.onRun());
  const replayBtn = document.createElement("button");
  replayBtn.textContent = "Replay";
  replayBtn.className = "secondary";
  replayBtn.disabled = true;
  replayBtn.addEventListener("click", () => cb.onReplay());
  btnRow.append(runBtn, replayBtn);
  controlsEl.appendChild(btnRow);

  controlsEl.appendChild(renderHeading("Display"));
  const themeRow = document.createElement("div");
  themeRow.className = "speed-row";
  const themeButtons = new Map<ThemeName, HTMLButtonElement>();
  for (const t of ["light", "dark"] as const) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "pill";
    b.textContent = t === "light" ? "Map" : "Tactical";
    if (t === DEFAULT_THEME) b.classList.add("is-active");
    b.addEventListener("click", () => {
      theme = t;
      for (const [k, el] of themeButtons) el.classList.toggle("is-active", k === t);
      document.documentElement.dataset.theme = t;
      cb.onThemeChange(t);
      legend.repaint();
    });
    themeButtons.set(t, b);
    themeRow.appendChild(b);
  }
  controlsEl.appendChild(themeRow);
  document.documentElement.dataset.theme = DEFAULT_THEME;

  controlsEl.appendChild(renderHeading("Playback"));
  const speedRow = document.createElement("div");
  speedRow.className = "speed-row";
  const speedButtons = new Map<number, HTMLButtonElement>();
  for (const s of SPEED_OPTIONS) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "pill";
    b.textContent = `${s}×`;
    if (s === DEFAULT_SPEED) b.classList.add("is-active");
    b.addEventListener("click", () => {
      currentSpeed = s;
      for (const [v, el] of speedButtons) el.classList.toggle("is-active", v === s);
      cb.onSpeedChange(s);
    });
    speedButtons.set(s, b);
    speedRow.appendChild(b);
  }
  controlsEl.appendChild(speedRow);

  const trailsBtn = document.createElement("button");
  trailsBtn.type = "button";
  trailsBtn.className = "toggle-btn";
  trailsBtn.textContent = "Trails: off";
  trailsBtn.addEventListener("click", () => {
    trailsOn = !trailsOn;
    trailsBtn.classList.toggle("is-active", trailsOn);
    trailsBtn.textContent = trailsOn ? "Trails: on" : "Trails: off";
    cb.onTrailsChange(trailsOn);
  });
  controlsEl.appendChild(trailsBtn);

  const cleanBtn = document.createElement("button");
  cleanBtn.type = "button";
  cleanBtn.className = "toggle-btn";
  cleanBtn.textContent = "Clean view: off";
  cleanBtn.addEventListener("click", () => {
    cleanView = !cleanView;
    cleanBtn.classList.toggle("is-active", cleanView);
    cleanBtn.textContent = cleanView ? "Clean view: on" : "Clean view: off";
    document.documentElement.classList.toggle("clean-view", cleanView);
    cb.onCleanViewChange(cleanView);
  });
  controlsEl.appendChild(cleanBtn);

  const legend = buildLegend((kind) => cb.onHighlight(kind), () => theme);
  controlsEl.appendChild(legend.element);

  toolbarEl.innerHTML = "";
  const playBtn = document.createElement("button");
  playBtn.type = "button";
  playBtn.className = "toolbar-icon-btn";
  playBtn.title = "Play / pause";
  playBtn.setAttribute("aria-label", "Play / pause");
  playBtn.innerHTML = playGlyph(false);
  playBtn.addEventListener("click", () => cb.onPauseToggle());
  toolbarEl.appendChild(playBtn);

  const contactBtn = document.createElement("button");
  contactBtn.type = "button";
  contactBtn.className = "toolbar-export-btn toolbar-contact-btn";
  contactBtn.innerHTML = `<svg width="11" height="11" viewBox="0 0 14 14" aria-hidden="true">
    <polygon points="1,2 7,7 1,12" fill="currentColor"/>
    <polygon points="7,2 13,7 7,12" fill="currentColor"/>
  </svg><span>Contact</span>`;
  contactBtn.title = "Jump to first detection";
  contactBtn.disabled = true;
  contactBtn.addEventListener("click", () => cb.onJumpToContact());
  toolbarEl.appendChild(contactBtn);

  const timeline = document.createElement("input");
  timeline.type = "range";
  timeline.className = "timeline";
  timeline.min = "0";
  timeline.max = "10";
  timeline.step = "0.05";
  timeline.value = "0";
  timeline.disabled = true;
  let scrubbing = false;
  timeline.addEventListener("input", () => {
    scrubbing = true;
    const v = Number.parseFloat(timeline.value);
    cb.onSeek(v);
  });
  timeline.addEventListener("change", () => { scrubbing = false; });
  toolbarEl.appendChild(timeline);

  const timeReadout = document.createElement("span");
  timeReadout.className = "timeline-readout";
  timeReadout.textContent = "0.0s";
  toolbarEl.appendChild(timeReadout);

  const exportBtn = document.createElement("button");
  exportBtn.type = "button";
  exportBtn.className = "toolbar-export-btn";
  exportBtn.textContent = "Export PNG";
  exportBtn.disabled = true;
  exportBtn.addEventListener("click", () => cb.onExportPng());
  toolbarEl.appendChild(exportBtn);

  const shareBtn = document.createElement("button");
  shareBtn.type = "button";
  shareBtn.className = "toolbar-export-btn";
  shareBtn.textContent = "Share setup";
  shareBtn.title = "Copy a link that reproduces this exact configuration";
  shareBtn.addEventListener("click", () => {
    void cb.onShareLink().then((ok) => {
      shareBtn.textContent = ok ? "Copied ✓" : "Share setup";
      setTimeout(() => {
        shareBtn.textContent = "Share setup";
      }, 1600);
    });
  });
  toolbarEl.appendChild(shareBtn);

  scorecardEl.innerHTML = "";
  scorecardEl.appendChild(renderHeading("Scorecard"));
  const scoreList = document.createElement("ul");
  scorecardEl.appendChild(scoreList);

  return {
    getConfig: () => ({ ...state }),
    getSpeed: () => currentSpeed,
    getTrails: () => trailsOn,
    getTheme: () => theme,
    setMetrics: (m) => {
      scoreList.innerHTML = "";
      if (!m) return;
      const ratio = m.initialTargetValue > 0 ? m.remainingTargetValue / m.initialTargetValue : 0;
      const items: Array<[string, string, "good" | "bad" | ""]> = [
        ["Target value preserved", `${(ratio * 100).toFixed(0)}%`, ratio > 0.7 ? "good" : ratio < 0.3 ? "bad" : ""],
        ["Targets destroyed", `${m.targetsDestroyed}`, ""],
        ["Targets hit", `${m.targetsHit}`, ""],
        ["Leakers", `${m.leakers}`, ""],
        ["Fixed shots", `${m.fixedDefenceShotsFired}`, ""],
        ["Mobile shots", `${m.mobileDefenceShotsFired}`, ""],
        ["Fixed util.", `${(m.fixedDefenceUtilisation * 100).toFixed(0)}%`, ""],
        ["Mobile util.", `${(m.mobileDefenceUtilisation * 100).toFixed(0)}%`, ""],
        ["Mean warning", `${m.warningTimeMean.toFixed(1)}s`, ""],
        ["Conc. efficiency", `${(m.concentrationEfficiency * 100).toFixed(0)}%`, ""],
        ["Attacker cost", `${m.attackerCost.toFixed(0)}`, ""],
        ["Defender cost", `${m.defenderCost.toFixed(0)}`, ""],
      ];
      for (const [k, v, cls] of items) {
        const li = document.createElement("li");
        li.innerHTML = `<span>${k}</span><span class="v ${cls}">${v}</span>`;
        scoreList.appendChild(li);
      }
    },
    setStatus: (s) => { overlayEl.textContent = s; },
    setReplayEnabled: (enabled) => { replayBtn.disabled = !enabled; },
    setPlaybackBounds: (end) => {
      timeline.max = `${Math.max(0.1, end).toFixed(2)}`;
      timeline.disabled = end <= 0;
    },
    setPlaybackTime: (t) => {
      if (!scrubbing) timeline.value = `${t}`;
      timeReadout.textContent = `${t.toFixed(1)}s`;
    },
    setPlaying: (on) => {
      playBtn.innerHTML = playGlyph(on);
    },
    setExportEnabled: (enabled) => {
      exportBtn.disabled = !enabled;
    },
    setContactEnabled: (enabled) => {
      contactBtn.disabled = !enabled;
    },
    applyConfig: (cfg) => {
      isApplyingPreset = true;
      for (const [k, v] of Object.entries(cfg)) {
        (state as unknown as Record<string, unknown>)[k] = v;
        const setter = setterByKey.get(k);
        if (setter) setter(v as never);
      }
      isApplyingPreset = false;
      presetSelect.value = "__custom__";
      presetDesc.textContent = "Shared configuration (from link).";
    },
  };
}

function playGlyph(playing: boolean): string {
  if (playing) {
    return `<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <rect x="2" y="2" width="3.5" height="10" fill="currentColor"/>
      <rect x="8.5" y="2" width="3.5" height="10" fill="currentColor"/>
    </svg>`;
  }
  return `<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <polygon points="3,2 12,7 3,12" fill="currentColor"/>
  </svg>`;
}

type LegendHandle = { element: HTMLElement; repaint: () => void };

function buildLegend(
  onChange: (kind: LegendKind | null) => void,
  getTheme: () => ThemeName,
): LegendHandle {
  const wrap = document.createElement("div");
  wrap.className = "legend";
  const rows: Array<[LegendKind, string]> = [
    ["target", "City"],
    ["fixed", "Fixed defence"],
    ["mobile", "CAP fighter"],
    ["sensor", "Sensor"],
    ["weapon", "Strike weapon"],
  ];
  let active: LegendKind | null = null;
  const elements = new Map<LegendKind, HTMLElement>();
  const canvases = new Map<LegendKind, HTMLCanvasElement>();

  for (const [kind, label] of rows) {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "legend-row";
    row.dataset.kind = kind;

    const icon = document.createElement("canvas");
    icon.className = "legend-icon";
    icon.width = 48;
    icon.height = 48;
    icon.setAttribute("aria-hidden", "true");
    row.appendChild(icon);

    const text = document.createElement("span");
    text.className = "legend-label";
    text.textContent = label;
    row.appendChild(text);

    row.addEventListener("click", () => {
      const next = active === kind ? null : kind;
      active = next;
      for (const [k, el] of elements) {
        el.classList.toggle("is-active", k === active);
        el.classList.toggle("is-dim", active !== null && k !== active);
      }
      onChange(active);
    });
    elements.set(kind, row);
    canvases.set(kind, icon);
    wrap.appendChild(row);
  }
  const hint = document.createElement("p");
  hint.className = "legend-hint";
  hint.textContent = "Click a row to isolate. Click again to clear.";
  wrap.appendChild(hint);

  const repaint = (): void => {
    const palette = getPalette(getTheme());
    for (const [kind, canvas] of canvases) paintLegendIcon(canvas, kind, palette);
  };
  repaint();

  return { element: wrap, repaint };
}

function paintLegendIcon(
  canvas: HTMLCanvasElement,
  kind: LegendKind,
  palette: ReturnType<typeof getPalette>,
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const cssSize = 24;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.floor(cssSize * dpr);
  canvas.height = Math.floor(cssSize * dpr);
  canvas.style.width = `${cssSize}px`;
  canvas.style.height = `${cssSize}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssSize, cssSize);
  const c = cssSize / 2;
  const pos = { x: c, y: c };
  switch (kind) {
    case "target":
      drawTarget(ctx, pos, 80, false, false, true, palette);
      break;
    case "fixed":
      drawFixedDefence(ctx, pos, palette);
      break;
    case "mobile":
      drawMobileDefence(ctx, pos, 0, palette);
      break;
    case "sensor":
      drawSensor(ctx, pos, palette);
      break;
    case "weapon":
      drawStrikeWeapon(ctx, pos, 0, true, palette);
      break;
  }
}

